import { ArrowUpToLine, Move, RotateCcw, TriangleAlert } from 'lucide-react';
import type { CaixaMovel, ConfigPlanner, ConfigPosicaoLivre, MedidasPagina } from '@miolos/core';
import { ROTULO_CAIXA_MOVEL, caixaLivrePadrao, caixaLivreEncaixada, encaixarCaixaLivre } from '@miolos/core';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Campo } from '@/components/ui/label';
import {
  camadaDa,
  comPosicao,
  posicoesDo,
  quantasPosicionadas,
  semPosicao,
  trazidaParaAFrente,
  type MapaDePosicoes,
} from '@/lib/posicoes-livres';
import { cn } from '@/lib/utils';

interface Props {
  config: ConfigPlanner;
  /** A régua da folha que está na prévia. */
  medidas: MedidasPagina;
  /** Caixas que existem nesta folha. */
  chaves: CaixaMovel[];
  /** A prévia está no modo de arrastar? */
  ligado: boolean;
  aoLigar: (ligado: boolean) => void;
  aoMudar: (posicoes: MapaDePosicoes) => void;
}

/**
 * O ajuste fino das caixas do dia.
 *
 * Duas formas de mexer na mesma posição: as alças em cima da prévia (para quem
 * quer ver) e os campos em milímetros (para quem quer o número exato). As duas
 * escrevem no mesmo mapa, então mudar aqui move a alça lá.
 */
export function AjusteDeCaixas({ config, medidas, chaves, ligado, aoLigar, aoMudar }: Props) {
  const posicoes = posicoesDo(config);
  const mexidas = chaves.filter((chave) => Boolean(posicoes[chave]));

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-0.5">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Posicionar as caixas do dia
          </h4>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {ligado
              ? 'Arraste o contorno na prévia para mover, e o quadradinho do canto para esticar. O desenho se move quando você solta. As setas do teclado ajustam de milímetro em milímetro.'
              : 'Ligue para tirar as caixas do lugar automático e escolher onde cada uma fica.'}
          </p>
        </div>
        <Button
          type="button"
          variant={ligado ? 'padrao' : 'contorno'}
          tamanho="pequeno"
          onClick={() => aoLigar(!ligado)}
          aria-pressed={ligado}
        >
          <Move />
          {ligado ? 'Terminar' : 'Posicionar'}
        </Button>
      </div>

      {ligado ? (
        <>
          <p className="rounded-md border border-border bg-muted/60 p-2.5 text-xs leading-relaxed text-muted-foreground">
            A posição vale para <strong className="font-semibold">todas as páginas de dia</strong> do miolo: é a mesma
            caixa em cada dia, do primeiro ao último. O que sai da área de conteúdo é trazido de volta para dentro da
            folha, e o aviso aparece aqui na prévia.
          </p>

          <ul className="space-y-2">
            {chaves.map((chave) => (
              <li key={chave}>
                {posicoes[chave] ? (
                  <CaixaEmMilimetros
                    chave={chave}
                    posicao={posicoes[chave]!}
                    medidas={medidas}
                    camada={camadaDa(config, chave)}
                    aoMudar={(nova) => aoMudar(comPosicao(config, chave, nova))}
                    aoVoltar={() => aoMudar(semPosicao(config, chave))}
                    aoTrazerParaFrente={() => aoMudar(trazidaParaAFrente(config, chave))}
                  />
                ) : (
                  <div
                    data-item-caixa={chave}
                    className="flex items-center justify-between gap-2 rounded-md border border-dashed border-border px-2.5 py-2"
                  >
                    <span className="truncate text-xs font-medium">{ROTULO_CAIXA_MOVEL[chave]}</span>
                    <Button
                      type="button"
                      variant="contorno"
                      tamanho="pequeno"
                      onClick={() =>
                        aoMudar(comPosicao(config, chave, caixaLivrePadrao(config, chave, quantasPosicionadas(config))))
                      }
                    >
                      Mover do meu jeito
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>

          {mexidas.length > 0 ? (
            <Button
              type="button"
              variant="fantasma"
              tamanho="pequeno"
              className="h-7 px-2 text-xs"
              onClick={() => aoMudar({})}
            >
              <RotateCcw />
              Devolver tudo ao lugar automático
            </Button>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

/** Uma caixa com os números à vista: x, y, largura e altura em milímetros. */
function CaixaEmMilimetros({
  chave,
  posicao,
  medidas,
  camada,
  aoMudar,
  aoVoltar,
  aoTrazerParaFrente,
}: {
  chave: CaixaMovel;
  posicao: ConfigPosicaoLivre;
  medidas: MedidasPagina;
  camada: number;
  aoMudar: (posicao: ConfigPosicaoLivre) => void;
  aoVoltar: () => void;
  aoTrazerParaFrente: () => void;
}) {
  const foraDaFolha = caixaLivreEncaixada(posicao, medidas);

  /*
   * O número digitado passa pela mesma régua do arrasto: quem sai da área de
   * conteúdo volta para dentro dela. Sem isto, o campo diria 500 mm e o desenho
   * sairia encostado na margem — dois números para a mesma caixa.
   */
  const ajustar = (alteracao: Partial<ConfigPosicaoLivre>) =>
    aoMudar(encaixarCaixaLivre({ ...posicao, ...alteracao }, medidas));

  const campo = (rotulo: string, valor: number, minimo: number, maximo: number, aplicar: (n: number) => void) => (
    <label className="space-y-1">
      <span className="block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{rotulo}</span>
      <Input
        type="number"
        inputMode="decimal"
        step={1}
        min={minimo}
        max={maximo}
        value={Math.round(valor * 10) / 10}
        onChange={(evento) => {
          if (evento.target.value === '') return;
          const numero = Number(evento.target.value);
          if (Number.isFinite(numero)) aplicar(numero);
        }}
        className="h-8 px-2 text-xs"
      />
    </label>
  );

  return (
    <div data-item-caixa={chave} className="space-y-2 rounded-md border border-primary/30 bg-primary/[0.04] px-2.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-xs font-semibold">{ROTULO_CAIXA_MOVEL[chave]}</span>
        <span className="flex items-center gap-1">
          <Button
            type="button"
            variant="fantasma"
            tamanho="icone"
            className="size-7"
            title="Trazer para a frente das outras caixas"
            aria-label="Trazer para a frente das outras caixas"
            onClick={aoTrazerParaFrente}
          >
            <ArrowUpToLine />
          </Button>
          <Button
            type="button"
            variant="fantasma"
            tamanho="icone"
            className="size-7"
            title="Voltar ao lugar que o gerador calcula"
            aria-label="Voltar ao lugar que o gerador calcula"
            onClick={aoVoltar}
          >
            <RotateCcw />
          </Button>
        </span>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {campo('Esquerda', posicao.xMm, 0, medidas.larguraConteudoMm, (xMm) => ajustar({ xMm }))}
        {campo('Topo', posicao.yMm, 0, medidas.alturaConteudoMm, (yMm) => ajustar({ yMm }))}
        {campo('Largura', posicao.larguraMm, 6, medidas.larguraConteudoMm, (larguraMm) => ajustar({ larguraMm }))}
        {campo('Altura', posicao.alturaMm, 6, medidas.alturaConteudoMm, (alturaMm) => ajustar({ alturaMm }))}
      </div>

      <p className={cn('text-[11px] leading-relaxed', foraDaFolha ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground')}>
        {foraDaFolha ? (
          <span className="flex items-start gap-1.5">
            <TriangleAlert className="mt-0.5 size-3 shrink-0" />
            Estes números passam da área de conteúdo: o gerador traz a caixa de volta para dentro da folha.
          </span>
        ) : (
          `Medido do canto de cima da área útil: ela tem ${Math.round(medidas.larguraConteudoMm)} × ${Math.round(
            medidas.alturaConteudoMm,
          )} mm nesta folha${camada > 0 ? ' · esta caixa fica na frente das outras' : ''}.`
        )}
      </p>
    </div>
  );
}
